import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  Collapse,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { Close, ExpandLess, ExpandMore } from "@mui/icons-material";
import { useImmerReducer } from "use-immer";
import { useDebounce } from "use-debounce";
import {
  CustomeDialog,
  InputControl,
  Loader,
  LoadingButton,
} from "../../../shared";
import leaf from "../../../assets/images/Icons/ingredient.svg";
import book from "../../../assets/images/Icons/Cookbook.svg";
import { useStockCategoryQueries } from "../hooks";
import { arraySort } from "../../../utils";

// ─── Helpers ──────────────────────────────────────────────

/** Collect all leaf item IDs from a category tree */
const getAllItemIds = (categories) => {
  const ids = [];
  for (const cat of categories) {
    for (const sub of cat.subData || []) {
      for (const item of sub.subData || []) {
        ids.push(item.id);
      }
    }
  }
  return ids;
};

/** Collect all leaf item IDs from a single subcategory */
const getSubCategoryItemIds = (sub) =>
  (sub.subData || []).map((item) => item.id);

/** Collect all leaf item IDs from a single category */
const getCategoryItemIds = (cat) =>
  (cat.subData || []).flatMap((sub) => getSubCategoryItemIds(sub));

// ─── Reducer ──────────────────────────────────────────────

const reducer = (draft, action) => {
  switch (action.type) {
    case "SET_SEARCH": {
      draft.search = action.payload;
      break;
    }
    case "TOGGLE_ITEM": {
      const id = action.payload;
      if (draft.selectedItemIds.includes(id)) {
        draft.selectedItemIds = draft.selectedItemIds.filter((i) => i !== id);
      } else {
        draft.selectedItemIds.push(id);
      }
      break;
    }
    case "TOGGLE_SUBCATEGORY": {
      const { ids } = action.payload;
      const allSelected = ids.every((id) => draft.selectedItemIds.includes(id));
      if (allSelected) {
        draft.selectedItemIds = draft.selectedItemIds.filter(
          (id) => !ids.includes(id),
        );
      } else {
        const toAdd = ids.filter((id) => !draft.selectedItemIds.includes(id));
        draft.selectedItemIds.push(...toAdd);
      }
      break;
    }
    case "TOGGLE_CATEGORY": {
      const { ids } = action.payload;
      const allSelected = ids.every((id) => draft.selectedItemIds.includes(id));
      if (allSelected) {
        draft.selectedItemIds = draft.selectedItemIds.filter(
          (id) => !ids.includes(id),
        );
      } else {
        const toAdd = ids.filter((id) => !draft.selectedItemIds.includes(id));
        draft.selectedItemIds.push(...toAdd);
      }
      break;
    }
    case "TOGGLE_ALL": {
      const { ids } = action.payload;
      const allSelected = ids.every((id) => draft.selectedItemIds.includes(id));
      if (allSelected) {
        draft.selectedItemIds = draft.selectedItemIds.filter(
          (id) => !ids.includes(id),
        );
      } else {
        const toAdd = ids.filter((id) => !draft.selectedItemIds.includes(id));
        draft.selectedItemIds.push(...toAdd);
      }
      break;
    }
    case "TOGGLE_EXPAND_CATEGORY": {
      const id = action.payload;
      if (draft.expandedCategories.includes(id)) {
        draft.expandedCategories = draft.expandedCategories.filter(
          (i) => i !== id,
        );
      } else {
        draft.expandedCategories.push(id);
      }
      break;
    }
    case "TOGGLE_EXPAND_SUBCATEGORY": {
      const id = action.payload;
      if (draft.expandedSubCategories.includes(id)) {
        draft.expandedSubCategories = draft.expandedSubCategories.filter(
          (i) => i !== id,
        );
      } else {
        draft.expandedSubCategories.push(id);
      }
      break;
    }
    case "RESET": {
      draft.selectedItemIds = [];
      draft.expandedCategories = [];
      draft.expandedSubCategories = [];
      draft.search = "";
      break;
    }
    default:
      break;
  }
};

// ─── Hook ─────────────────────────────────────────────────

const useAddStockItemsDialog = ({ open, existingItemIds = [] }) => {
  const [state, dispatch] = useImmerReducer(reducer, {
    selectedItemIds: [],
    expandedCategories: [],
    expandedSubCategories: [],
    search: "",
  });

  const [debouncedText] = useDebounce(state.search, 1000);

  const {
    listQuery: { data: categories = [], isLoading },
  } = useStockCategoryQueries({
    categorylist: true,
    debouncedText,
  });

  useEffect(() => {
    if (open) {
      dispatch({ type: "RESET" });
    }
  }, [open, dispatch]);

  const updateSearch = (value) => {
    dispatch({ type: "SET_SEARCH", payload: value });
  };

  const toggleItem = (id) => {
    if (existingItemIds.includes(id)) return;
    dispatch({ type: "TOGGLE_ITEM", payload: id });
  };

  const toggleSubCategory = (sub) => {
    const ids = getSubCategoryItemIds(sub).filter(
      (id) => !existingItemIds.includes(id),
    );
    if (!ids.length) return;
    dispatch({ type: "TOGGLE_SUBCATEGORY", payload: { ids } });
  };

  const toggleCategory = (cat) => {
    const ids = getCategoryItemIds(cat).filter(
      (id) => !existingItemIds.includes(id),
    );
    if (!ids.length) return;
    dispatch({ type: "TOGGLE_CATEGORY", payload: { ids } });
  };

  const toggleAll = () => {
    const ids = getAllItemIds(categories).filter(
      (id) => !existingItemIds.includes(id),
    );
    if (!ids.length) return;
    dispatch({ type: "TOGGLE_ALL", payload: { ids } });
  };

  const toggleExpandCategory = (id) => {
    dispatch({ type: "TOGGLE_EXPAND_CATEGORY", payload: id });
  };

  const toggleExpandSubCategory = (id) => {
    dispatch({ type: "TOGGLE_EXPAND_SUBCATEGORY", payload: id });
  };

  /** Check state for an array of IDs */
  const getCheckState = (ids) => {
    const checked = ids.filter(
      (id) =>
        state.selectedItemIds.includes(id) || existingItemIds.includes(id),
    );
    const all = checked.length === ids.length && ids.length > 0;
    const indeterminate = checked.length > 0 && !all;
    return { checked: all, indeterminate };
  };

  return {
    state,
    categories,
    isLoading,
    toggleItem,
    toggleSubCategory,
    toggleCategory,
    toggleAll,
    toggleExpandCategory,
    toggleExpandSubCategory,
    getCheckState,
    updateSearch,
  };
};

// ─── Component ────────────────────────────────────────────

export const AddStockItemsDialog = ({
  open,
  onClose,
  onAdd,
  existingItemIds = [],
  isAddingItems,
}) => {
  const {
    state,
    categories,
    isLoading,
    toggleItem,
    toggleSubCategory,
    toggleCategory,
    toggleAll,
    toggleExpandCategory,
    toggleExpandSubCategory,
    getCheckState,
    updateSearch,
  } = useAddStockItemsDialog({ open, existingItemIds });

  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    if (!open) setSearchInput("");
  }, [open]);

  const onSearch = (e) => {
    setSearchInput(e.target.value);
    updateSearch(e.target.value);
  };

  const allItemIds = getAllItemIds(categories);
  const allCheck = getCheckState(allItemIds);
  const allDisabled =
    allItemIds.length > 0 &&
    allItemIds.every((id) => existingItemIds.includes(id));

  const handleAdd = () => {
    onAdd(state.selectedItemIds);
  };

  return (
    <CustomeDialog
      open={open}
      handleClose={onClose}
      title={
        <Stack
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          p="16px"
          borderBottom={1}
          borderColor="divider"
          height="52px"
          width="100%"
          gap="8px"
        >
          <Typography fontWeight={500} variant="body1" fontSize="20px">
            Items
          </Typography>
          <InputControl
            type="table-search"
            size="small"
            placeholder="Search by name or Category"
            value={searchInput}
            onChange={onSearch}
            sx={{
              minWidth: "240px",
              fontSize: "14px",
            }}
            endAdornment={
              searchInput && (
                <InputAdornment
                  position="end"
                  onClick={() => onSearch({ target: { value: "" } })}
                  sx={{ cursor: "pointer" }}
                >
                  <Close size="small" />
                </InputAdornment>
              )
            }
          />
        </Stack>
      }
      divider={false}
      content={
        <Box sx={{ height: "52vh", overflow: "auto" }}>
          {isLoading ? (
            <Loader />
          ) : categories.length === 0 ? (
            <Stack justifyContent="center" alignItems="center" height="52vh">
              <Typography variant="caption" color="text.secondary">
                No items found
              </Typography>
            </Stack>
          ) : (
            <List dense disablePadding sx={{ m: 1 }}>
              {/* All Items */}
              <ListItemButton
                dense
                onClick={toggleAll}
                sx={{ borderRadius: "6px" }}
              >
                <Checkbox
                  edge="end"
                  checked={allCheck.checked}
                  indeterminate={allCheck.indeterminate}
                  disabled={allDisabled}
                  disableRipple
                  size="small"
                />
                <ListItemText
                  primary="All Items"
                  primaryTypographyProps={{ fontWeight: 500, fontSize: "14px" }}
                />
              </ListItemButton>

              {categories.map((cat) => (
                <CategoryRow
                  key={cat.id}
                  category={cat}
                  state={state}
                  existingItemIds={existingItemIds}
                  toggleCategory={toggleCategory}
                  toggleSubCategory={toggleSubCategory}
                  toggleItem={toggleItem}
                  toggleExpandCategory={toggleExpandCategory}
                  toggleExpandSubCategory={toggleExpandSubCategory}
                  getCheckState={getCheckState}
                />
              ))}
            </List>
          )}
        </Box>
      }
      footer={{
        display: true,
        cancelButton: "Cancel",
        onCancel: onClose,
        confirmButton: (
          <LoadingButton
            variant="contained"
            size="small"
            onClick={handleAdd}
            loading={isAddingItems}
            disabled={state.selectedItemIds.length === 0}
          >
            Add
          </LoadingButton>
        ),
        onConfirm: true,
      }}
      sx={{
        pt: 0,
        px: "2px",
        pb: "8px",
      }}
      PaperProps={{
        sx: { width: { xs: "100%", sm: "520px", md: "650px" } },
      }}
    />
  );
};

// ────────────────────────────────────────────────────── Sub-Components ──────────────────────────────────────────────────────────────────

const CategoryRow = ({
  category,
  state,
  existingItemIds,
  toggleCategory,
  toggleSubCategory,
  toggleItem,
  toggleExpandCategory,
  toggleExpandSubCategory,
  getCheckState,
}) => {
  const catItemIds = getCategoryItemIds(category);
  const catCheck = getCheckState(catItemIds);
  const isExpanded = state.expandedCategories.includes(category.id);
  const allExisting =
    catItemIds.length > 0 &&
    catItemIds.every((id) => existingItemIds.includes(id));

  return (
    <>
      <ListItemButton
        dense
        onClick={() => toggleExpandCategory(category.id)}
        sx={{
          bgcolor: isExpanded ? "background.default" : "transparent",
          borderTopLeftRadius: "6px",
          borderTopRightRadius: "6px",
          borderBottomRightRadius: isExpanded ? "0" : "6px",
          borderBottomLeftRadius: isExpanded ? "0" : "6px",
        }}
      >
        <Checkbox
          edge="end"
          checked={catCheck.checked}
          indeterminate={catCheck.indeterminate}
          disabled={allExisting}
          disableRipple
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (!allExisting) toggleCategory(category);
          }}
        />
        <ListItemText
          primary={category.name}
          primaryTypographyProps={{ fontWeight: 500, fontSize: "14px" }}
        />
        {isExpanded ? (
          <ExpandLess fontSize="small" />
        ) : (
          <ExpandMore fontSize="small" />
        )}
      </ListItemButton>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <List
          dense
          disablePadding
          sx={{
            bgcolor: isExpanded ? "background.default" : "transparent",
            borderBottomRightRadius: "6px",
            borderBottomLeftRadius: "6px",
          }}
        >
          {(category.subData || []).map((sub) => (
            <SubCategoryRow
              key={sub.id}
              subCategory={sub}
              state={state}
              existingItemIds={existingItemIds}
              toggleSubCategory={toggleSubCategory}
              toggleItem={toggleItem}
              toggleExpandSubCategory={toggleExpandSubCategory}
              getCheckState={getCheckState}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
};

const SubCategoryRow = ({
  subCategory,
  state,
  existingItemIds,
  toggleSubCategory,
  toggleItem,
  toggleExpandSubCategory,
  getCheckState,
}) => {
  const subItemIds = getSubCategoryItemIds(subCategory);
  const subCheck = getCheckState(subItemIds);
  const isExpanded = state.expandedSubCategories.includes(subCategory.id);
  const hasItems = subItemIds.length > 0;
  const allExisting =
    subItemIds.length > 0 &&
    subItemIds.every((id) => existingItemIds.includes(id));

  return (
    <>
      <ListItemButton
        dense
        sx={{ pl: 4 }}
        onClick={() => hasItems && toggleExpandSubCategory(subCategory.id)}
      >
        <Checkbox
          edge="end"
          checked={subCheck.checked}
          indeterminate={subCheck.indeterminate}
          disabled={allExisting}
          disableRipple
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (!allExisting) toggleSubCategory(subCategory);
          }}
        />
        <ListItemText
          primary={subCategory.name}
          primaryTypographyProps={{ fontWeight: 500, fontSize: "14px" }}
        />
        {hasItems &&
          (isExpanded ? (
            <ExpandLess fontSize="small" />
          ) : (
            <ExpandMore fontSize="small" />
          ))}
      </ListItemButton>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <List dense disablePadding>
          {(arraySort(subCategory.subData, "name") || []).map((item) => {
            const isExisting = existingItemIds.includes(item.id);
            const isChecked =
              isExisting || state.selectedItemIds.includes(item.id);
            return (
              <ListItemButton
                key={item.id}
                dense
                sx={{ pl: 7 }}
                onClick={() => toggleItem(item.id)}
              >
                <Checkbox
                  edge="end"
                  checked={isChecked}
                  disabled={isExisting}
                  disableRipple
                  size="small"
                />
                {item.type === "recipe" ? (
                  <img src={book} alt="recipe" width={16} height={16} />
                ) : (
                  <img src={leaf} alt="ingredient" width={16} height={16} />
                )}
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{ fontSize: "14px" }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Collapse>
    </>
  );
};
